import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { platformApi } from '@/services/platform.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Platform-wide SMTP administration.
 *
 * Not a tenant page — every request here is gated on `platform.settings.*`,
 * which only `PLATFORM_SUPER_ADMIN` ever holds. A saved row overrides the
 * SMTP_* env vars the service would otherwise fall back to; "Send a test
 * email" always exercises whatever is currently saved, for real, against
 * the real SMTP server — never a simulated response.
 */
export function SmtpSettingsPage() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['platform', 'smtp-settings'],
    queryFn: () => platformApi.getSmtpSettings(),
  });

  // Each field is `null` until the caller edits it — the visible value falls
  // back to whatever the query loaded, the same pattern administration-page's
  // tenant-rename field already uses, so there is no effect needed to "seed"
  // the form once data arrives.
  const [host, setHost] = useState<string | null>(null);
  const [port, setPort] = useState<string | null>(null);
  const [useImplicitTls, setUseImplicitTls] = useState<boolean | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [senderAddress, setSenderAddress] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSent, setTestSent] = useState(false);

  const showPasswordInput = isChangingPassword || settings.data?.hasPassword === false;

  const save = useMutation({
    mutationFn: () =>
      platformApi.updateSmtpSettings({
        host: host ?? settings.data?.host ?? '',
        port: Number(port ?? settings.data?.port ?? 587),
        useImplicitTls: useImplicitTls ?? settings.data?.useImplicitTls ?? false,
        username: username ?? settings.data?.username ?? '',
        password: showPasswordInput && password ? password : undefined,
        senderAddress: senderAddress ?? settings.data?.senderAddress ?? '',
        replyTo: (replyTo ?? settings.data?.replyTo) || null,
      }),
    onSuccess: async () => {
      setSaveError(null);
      setIsChangingPassword(false);
      setPassword('');
      await queryClient.invalidateQueries({ queryKey: ['platform', 'smtp-settings'] });
    },
    onError: (error: unknown) => {
      setSaveError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const sendTest = useMutation({
    mutationFn: () => platformApi.sendTestEmail(testTo),
    onSuccess: () => {
      setTestError(null);
      setTestSent(true);
    },
    onError: (error: unknown) => {
      setTestSent(false);
      setTestError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: error.message })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.smtp.title')}</h1>
          <p className="page-count">{t('platform.smtp.subtitle')}</p>
        </div>
      </header>

      {settings.isError && (
        <Alert tone="danger">
          {settings.error instanceof ApiError
            ? t(`errors.${settings.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {settings.data && (
        <div className="settings-status">
          <div>
            <p className="settings-status-title">{t('platform.smtp.currentStatus')}</p>
            <p className="settings-status-hint">
              {t(`platform.smtp.source.${settings.data.source}`)}
            </p>
            {settings.data.updatedAt && (
              <p className="settings-status-hint">
                {t('platform.smtp.lastUpdate', {
                  values: { date: formatDateTime(settings.data.updatedAt, locale) },
                })}
              </p>
            )}
          </div>
          <span
            className="badge"
            data-status={settings.data.source === 'database' ? 'ACTIVE' : undefined}
          >
            {t(`platform.smtp.sourceBadge.${settings.data.source}`)}
          </span>
        </div>
      )}

      <form
        className="settings-card"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <h2 className="settings-card-title">{t('platform.smtp.server')}</h2>

        <div className="form-grid">
          <Field
            label={t('platform.smtp.host')}
            name="host"
            value={host ?? settings.data?.host ?? ''}
            onChange={(event) => setHost(event.target.value)}
            required
          />
          <Field
            label={t('platform.smtp.port')}
            name="port"
            type="number"
            min={1}
            max={65535}
            value={port ?? String(settings.data?.port ?? 587)}
            onChange={(event) => setPort(event.target.value)}
            required
          />
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={useImplicitTls ?? settings.data?.useImplicitTls ?? false}
            onChange={(event) => setUseImplicitTls(event.target.checked)}
          />
          <span>{t('platform.smtp.implicitTls')}</span>
        </label>

        <div className="form-grid">
          <Field
            label={t('platform.smtp.username')}
            name="username"
            value={username ?? settings.data?.username ?? ''}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
          <div className="field">
            <div className="settings-password-label">
              <label className="field-label" htmlFor="smtp-password">
                {t('platform.smtp.password')}
              </label>
              {!showPasswordInput && (
                <button
                  type="button"
                  className="settings-inline-link"
                  onClick={() => setIsChangingPassword(true)}
                >
                  {t('platform.smtp.changePassword')}
                </button>
              )}
            </div>
            {showPasswordInput ? (
              <input
                id="smtp-password"
                type="password"
                className="field-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required={settings.data?.hasPassword === false}
              />
            ) : (
              <p className="settings-password-stored">{t('platform.smtp.passwordStored')}</p>
            )}
          </div>
        </div>

        <div className="form-grid">
          <Field
            label={t('platform.smtp.senderAddress')}
            name="senderAddress"
            type="email"
            value={senderAddress ?? settings.data?.senderAddress ?? ''}
            onChange={(event) => setSenderAddress(event.target.value)}
            required
          />
          <Field
            label={t('platform.smtp.replyTo')}
            name="replyTo"
            type="email"
            value={replyTo ?? settings.data?.replyTo ?? ''}
            onChange={(event) => setReplyTo(event.target.value)}
            hint={t('platform.smtp.optional')}
          />
        </div>

        {saveError && <Alert tone="danger">{saveError}</Alert>}

        <Button type="submit" variant="primary" loading={save.isPending}>
          {t('common.actions.save')}
        </Button>
      </form>

      <form
        className="settings-card settings-card-test"
        onSubmit={(event) => {
          event.preventDefault();
          setTestSent(false);
          sendTest.mutate();
        }}
      >
        <h2 className="settings-card-title">{t('platform.smtp.sendTest')}</h2>

        {testSent && <Alert tone="success">{t('platform.smtp.testSent')}</Alert>}
        {testError && <Alert tone="danger">{testError}</Alert>}

        <div className="settings-test-row">
          <Field
            label={t('platform.smtp.testRecipient')}
            name="testTo"
            type="email"
            value={testTo}
            onChange={(event) => setTestTo(event.target.value)}
            placeholder="test@destino.com"
            required
          />
          <Button type="submit" variant="secondary" loading={sendTest.isPending}>
            {t('platform.smtp.sendTestAction')}
          </Button>
        </div>
      </form>
    </div>
  );
}
